import React, { useEffect, useState } from 'react';

import { Paper, Stack } from '@mui/material';

import type { SetupChecklistRow } from '../setupRules';
import type { ManualApprovalState } from '../types';

import ChecklistRuleCard from './ChecklistRuleCard';
import HiddenApprovedFooter from './HiddenApprovedFooter';

interface Props {
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<SetupChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
}

export default function SetupReportCard({
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedRows(rows.reduce<Record<string, boolean>>((nextRows, row) => ({ ...nextRows, [row.id]: true }), {}));
  }, [rows]);

  return (
    <Stack spacing={1.5}>
      {rows.length === 0 ? (
        <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
          No setup rows match this view.
        </Paper>
      ) : (
        rows.map((row) => {
          const manuallyApproved = !!manualApprovals[row.id];
          const approved = row.status === 'approved' || manuallyApproved;
          const isExpanded = expandedRows[row.id] ?? true;

          return (
            <ChecklistRuleCard
              key={row.id}
              approved={approved}
              dataPoints={row.evidence.map((evidence) => ({
                error: evidence.error,
                label: evidence.label,
                readOnly: true,
                value: getInputValue(evidence),
              }))}
              dataPointsLabel="Validation inputs"
              expanded={isExpanded}
              manuallyApproved={manuallyApproved}
              rule={row.rule}
              summary={row.summary}
              title={row.field}
              onApprovalChange={() => onApprovalChange(row.id)}
              onToggleExpanded={() => setExpandedRows((currentRows) => ({ ...currentRows, [row.id]: !isExpanded }))}
            />
          );
        })
      )}
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="setup row" showAutoApproved={showAutoApproved} />
    </Stack>
  );
}

function getInputValue(evidence: SetupChecklistRow['evidence'][number]) {
  return evidence.canOverride && evidence.value === '-' ? '' : evidence.value;
}
